/**
 * RecordingTeleprompter — Floating, draggable panel with script area,
 * speed/opacity sliders, AI mic button, and auto-scroll playback.
 *
 * Opacity controls everything EXCEPT the user's script text.
 * Draggable via the header bar.
 *
 * The AI microphone lets the user record a rough idea; the system
 * combines it with the current canvas diagram to generate a
 * presentation script that streams into the text area.
 */

import React, { useRef, useEffect, useState, useCallback } from "react";

import { useAppI18n } from "../hooks/useAppI18n";
import { getBackendUrl } from "../utils/backendUrl";

import { TeleprompterStreamFetch } from "../utils/TeleprompterStreamFetch";
import { getCanvasElements } from "../utils/canvasDataBridge";

import { VoiceInputButton } from "./VoiceInputButton";

interface RecordingTeleprompterProps {
  isOpen: boolean;
  script: string;
  speed: number; // 1–10
  opacity: number; // 0–100
  isPlaying: boolean;
  onClose: () => void;
  onScriptChange: (text: string) => void;
  onSpeedChange: (speed: number) => void;
  onOpacityChange: (opacity: number) => void;
  onTogglePlayback: () => void;
}

export const RecordingTeleprompter: React.FC<RecordingTeleprompterProps> = ({
  isOpen,
  script,
  speed,
  opacity,
  isPlaying,
  onClose,
  onScriptChange,
  onSpeedChange,
  onOpacityChange,
  onTogglePlayback,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);

  // ── Drag state ────────────────────────────────────────────────────────────
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  // ── AI generation state ─────────────────────────────────────────────────
  const [isGenerating, setIsGenerating] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (
        (e.target as HTMLElement).closest("button") ||
        (e.target as HTMLElement).closest("input")
      ) {
        return;
      }
      e.preventDefault();
      setIsDragging(true);
      dragStart.current = {
        mx: e.clientX,
        my: e.clientY,
        px: position.x,
        py: position.y,
      };
    },
    [position],
  );

  useEffect(() => {
    if (!isDragging) {
      return;
    }
    const onMove = (e: MouseEvent) => {
      setPosition({
        x: dragStart.current.px + (e.clientX - dragStart.current.mx),
        y: dragStart.current.py + (e.clientY - dragStart.current.my),
      });
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging]);

  // ── Auto-scroll playback ──────────────────────────────────────────────────
  // Sub-pixel accumulator so fractional scroll amounts aren't lost
  const scrollAccumRef = useRef(0);

  useEffect(() => {
    if (!isPlaying || !scrollRef.current) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    const el = scrollRef.current;
    scrollAccumRef.current = 0;

    // speed 1→5 px/s, 5→15, 10→30 px/s
    const pxPerSecond = 5 + (speed - 1) * (25 / 9);
    let lastTime = performance.now();

    const tick = (now: number) => {
      const dt = (now - lastTime) / 1000;
      lastTime = now;

      scrollAccumRef.current += pxPerSecond * dt;
      const whole = Math.floor(scrollAccumRef.current);
      if (whole >= 1) {
        scrollAccumRef.current -= whole;
        el.scrollTop += whole;
      }

      if (el.scrollTop >= el.scrollHeight - el.clientHeight) {
        animationRef.current = null;
        return;
      }
      animationRef.current = requestAnimationFrame(tick);
    };

    animationRef.current = requestAnimationFrame(tick);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isPlaying, speed]);

  // ── AI script generation ──────────────────────────────────────────────────
  const streamedTextRef = useRef("");

  const handleVoiceTranscript = useCallback(
    async (transcript: string) => {
      if (isGenerating) {
        return;
      }

      const backendUrl = getBackendUrl();
      if (!backendUrl) {
        console.error("[Teleprompter] Backend URL not configured");
        return;
      }

      setIsGenerating(true);
      abortRef.current = new AbortController();

      const elements = getCanvasElements();
      const canvasData = elements.length > 0 ? [...elements] : null;

      const previousScript = script.trim() || null;

      // Reset streaming accumulator and clear the text area
      streamedTextRef.current = "";
      onScriptChange("");

      try {
        const result = await TeleprompterStreamFetch({
          url: `${backendUrl}/v1/ai/teleprompter/generate`,
          prompt: transcript,
          canvasElements: canvasData as any,
          previousScript,
          signal: abortRef.current.signal,
          onChunk: (delta) => {
            streamedTextRef.current += delta;
            onScriptChange(streamedTextRef.current);
          },
        });

        if (result.error) {
          console.error("[Teleprompter] Generation error:", result.error);
          onScriptChange(previousScript || `Error: ${result.error.message}`);
        } else if (result.script) {
          onScriptChange(result.script);
        }
      } catch (err) {
        console.error("[Teleprompter] Unexpected error:", err);
      } finally {
        setIsGenerating(false);
        abortRef.current = null;
      }
    },
    [isGenerating, script, onScriptChange],
  );

  const { t } = useAppI18n();

  if (!isOpen) {
    return null;
  }

  // ── Opacity logic ─────────────────────────────────────────────────────────
  const panelAlpha = opacity / 100;
  const chromeAlpha = Math.max(0.15, panelAlpha);

  return (
    <div
      className="recording-teleprompter"
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        backgroundColor: `rgba(255, 255, 255, ${panelAlpha})`,
        borderColor: `rgba(214, 211, 209, ${panelAlpha * 0.7})`,
        boxShadow:
          panelAlpha > 0.3
            ? `0 20px 40px -8px rgba(0, 0, 0, ${
                0.12 * panelAlpha
              }), 0 8px 16px -4px rgba(0, 0, 0, ${0.06 * panelAlpha})`
            : `0 2px 12px rgba(0, 0, 0, ${0.05 * panelAlpha})`,
      }}
    >
      {/* Header — draggable */}
      <div
        className="recording-teleprompter__header"
        style={{
          borderBottomColor: `rgba(231, 229, 228, ${panelAlpha})`,
          cursor: isDragging ? "grabbing" : "grab",
        }}
        onMouseDown={handleDragStart}
      >
        <div
          className="recording-teleprompter__title-row"
          style={{ opacity: chromeAlpha }}
        >
          <span className="material-symbols-outlined recording-teleprompter__icon">
            description
          </span>
          <span className="recording-teleprompter__title">
            {t("recording.teleprompterTitle")}
          </span>
        </div>
        <button
          type="button"
          className="recording-teleprompter__close"
          onClick={onClose}
          aria-label={t("recording.close")}
          style={{ opacity: chromeAlpha }}
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      {/* Controls */}
      <div
        className="recording-teleprompter__controls"
        style={{
          borderBottomColor: `rgba(231, 229, 228, ${panelAlpha})`,
          opacity: chromeAlpha,
        }}
      >
        <button
          type="button"
          className="recording-teleprompter__play-btn"
          onClick={onTogglePlayback}
          title={
            isPlaying
              ? t("recording.pauseScrolling")
              : t("recording.startScrolling")
          }
        >
          <span className="material-symbols-outlined">
            {isPlaying ? "pause" : "play_arrow"}
          </span>
        </button>

        <div className="recording-teleprompter__sliders">
          <div className="recording-teleprompter__slider-row">
            <span className="recording-teleprompter__slider-label">
              {t("recording.speed")}
            </span>
            <input
              type="range"
              min={1}
              max={10}
              value={speed}
              onChange={(e) => onSpeedChange(Number(e.target.value))}
              className="recording-teleprompter__slider"
            />
          </div>
          <div className="recording-teleprompter__slider-row">
            <span className="recording-teleprompter__slider-label">
              {t("recording.opacity")}
            </span>
            <input
              type="range"
              min={20}
              max={100}
              value={opacity}
              onChange={(e) => onOpacityChange(Number(e.target.value))}
              className="recording-teleprompter__slider"
            />
          </div>
        </div>

        {/* AI microphone — generates script from voice + diagram */}
        <div
          className="recording-teleprompter__ai-mic"
          data-tooltip={t("recording.aiMicTooltip")}
        >
          <VoiceInputButton
            onTranscript={handleVoiceTranscript}
            disabled={isGenerating || isPlaying}
            layout="reverse"
            className="recording-teleprompter__mic-btn"
            allowEmptyTranscript
            disablePostProcess
          />
          <span className="recording-teleprompter__ai-badge">AI</span>
        </div>
      </div>

      {/* Script area — scrollable container for auto-scroll playback */}
      <div className="recording-teleprompter__body" ref={scrollRef}>
        {isPlaying ? (
          <div className="recording-teleprompter__script-display">
            {script || t("recording.noScript")}
          </div>
        ) : (
          <>
            {isGenerating && (
              <div className="recording-teleprompter__generating-indicator">
                <span className="recording-teleprompter__generating-dot" />
                <span>{t("recording.generatingScript")}</span>
              </div>
            )}
            <textarea
              className="recording-teleprompter__textarea"
              placeholder={t("recording.scriptPlaceholder")}
              value={script}
              onChange={(e) => onScriptChange(e.target.value)}
              readOnly={isGenerating}
            />
          </>
        )}
      </div>
    </div>
  );
};
